import React, { useState, useEffect } from "react";
import PageTableHeader from "../../components/pageTableHeader/pageTableHeader";
import { useNavigate } from "react-router-dom";
import { getReimbursementSummary, getAllExpenses } from "../../../services/workspace/reimbursement.service";
import { toast } from "react-hot-toast";
import DefaultButton from "../../components/buttons/defaultButton";
import DefaultBadge from "../../components/badge/defaultBadge";
import { usePermissionCheck, useUserRole } from "../../../hooks";
import {
  FiDollarSign,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiTrendingUp,
  FiPlus,
  FiShield,
  FiAlertTriangle,
  FiActivity
} from "react-icons/fi";
import moment from "moment";
import { _userRole } from "../../../utils/common";

const items = [{ label: "Reimbursement Dashboard", path: "" }];

const ReimbursementDashboard = () => {
  const role = useUserRole();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);

  // Check permissions for dashboard access - user must have approvals-approve permission
  const approvalsPermissions = usePermissionCheck("reimbursement", "approvals");
  const financePermissions = usePermissionCheck("reimbursement", "finance");

  const canViewApprovals = approvalsPermissions.canView;
  const canViewFinance = financePermissions.canView;
  const canApprove = approvalsPermissions.hasPermission("approve"); // Check for approvals-approve permission

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryResponse, expensesResponse] = await Promise.all([
        getReimbursementSummary({ period: "current_month" }),
        getAllExpenses({ limit: 5 }),
      ]);

      setSummary(summaryResponse?.data || null);
      // Safely extract expenses array
      const expenses = expensesResponse?.data?.data || expensesResponse?.data || expensesResponse || [];
      setRecentExpenses(Array.isArray(expenses) ? expenses : []);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to fetch dashboard data");
      // Set default values on error
      setSummary(null);
      setRecentExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has approve permission to view dashboard
  if (!loading && !canApprove && role !== _userRole.WORKSPACE_ADMIN) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <p className="text-lg font-semibold text-gray-900">Access Denied</p>
        <p className="text-sm text-gray-600">You don't have permission to view the Reimbursement Dashboard.</p>
        <p className="text-xs text-gray-500">Required permission: approvals-approve</p>
        <DefaultButton
          name="Go to My Expenses"
          variant="primary"
          size="md"
          onClick={() => navigate("/reimbursement/my-expenses")}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <PageTableHeader breadcrumbs={items} />
        {/* Skeleton KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            "border-l-blue-500",
            "border-l-amber-500",
            "border-l-green-500",
            "border-l-purple-500",
          ].map((accent, i) => (
            <div
              key={i}
              className={`bg-white p-5 rounded-xl border border-borderColor border-l-[3px] ${accent} animate-pulse`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-3">
                  <div className="h-3 w-28 bg-gray-200 rounded" />
                  <div className="h-7 w-20 bg-gray-200 rounded" />
                  <div className="h-3 w-16 bg-gray-100 rounded" />
                </div>
                <div className="w-12 h-12 rounded-full bg-gray-100" />
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton Quick Actions */}
        <div className="animate-pulse">
          <div className="h-5 w-32 bg-gray-200 rounded mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-borderColor">
                <div className="w-11 h-11 rounded-full bg-gray-100 mb-3" />
                <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                <div className="h-3 w-32 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        </div>
        {/* Skeleton Recent Expenses */}
        <div className="bg-white p-5 rounded-xl border border-borderColor animate-pulse">
          <div className="h-5 w-36 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                <div className="w-11 h-11 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-28 bg-gray-200 rounded" />
                  <div className="h-3 w-40 bg-gray-100 rounded" />
                </div>
                <div className="h-5 w-16 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { label: "Draft", color: "gray" },
    SUBMITTED: { label: "Submitted", color: "blue" },
    PENDING_APPROVAL: { label: "Pending Approval", color: "yellow" },
    APPROVED: { label: "Approved", color: "green" },
    REJECTED: { label: "Rejected", color: "red" },
    RETURNED: { label: "Returned", color: "orange" },
    PROCESSING: { label: "Processing", color: "indigo" },
    PAID: { label: "Paid", color: "emerald" },
    ADDED_TO_PAYROLL: { label: "Added to Payroll", color: "purple" },
    INSTANT_PAYOUT: { label: "Instant Payout", color: "green" },
    PAYROLL: { label: "Payroll", color: "purple" },
  };

  const kpiCards = [
    {
      title: "Total Expenses This Month",
      value: `₹${summary?.total_amount?.toLocaleString() || 0}`,
      icon: <FiDollarSign className="text-xl text-blue-600" />,
      bgColor: "bg-blue-50",
      ringColor: "ring-blue-100",
      accentColor: "border-l-blue-500",
      change: summary?.change_percentage || 0,
    },
    {
      title: "Pending Approvals",
      value: summary?.pending_approval_count || 0,
      icon: <FiClock className="text-xl text-amber-600" />,
      bgColor: "bg-amber-50",
      ringColor: "ring-amber-100",
      accentColor: "border-l-amber-500",
      subtext: `₹${summary?.pending_approval_amount?.toLocaleString() || 0}`,
    },
    {
      title: "Approved This Month",
      value: summary?.approved_count || 0,
      icon: <FiCheckCircle className="text-xl text-green-600" />,
      bgColor: "bg-green-50",
      ringColor: "ring-green-100",
      accentColor: "border-l-green-500",
      subtext: `₹${summary?.approved_amount?.toLocaleString() || 0}`,
    },
    {
      title: "Total Expenses",
      value: summary?.total_expense_count || 0,
      icon: <FiFileText className="text-xl text-purple-600" />,
      bgColor: "bg-purple-50",
      ringColor: "ring-purple-100",
      accentColor: "border-l-purple-500",
      subtext: "All time",
    },
  ];

  const quickActions = [
    {
      title: "Submit New Expense",
      description: "Create and submit a new expense report",
      icon: <FiPlus className="text-xl text-white" />,
      primary: true,
      onClick: () => navigate("/reimbursement/my-expenses"),
    },
    ...(canViewApprovals
      ? [
          {
            title: "Pending Approvals",
            description: "Review and approve submitted expenses",
            icon: <FiClock className="text-xl text-amber-600" />,
            iconBg: "bg-amber-50",
            onClick: () => navigate("/reimbursement/manager-approvals"),
          },
        ]
      : []),
    ...(canViewFinance
      ? [
          {
            title: "Finance Queue",
            description: "Process approved expenses for payment",
            icon: <FiDollarSign className="text-xl text-green-600" />,
            iconBg: "bg-green-50",
            onClick: () => navigate("/reimbursement/finance-queue"),
          },
        ]
      : []),
  ];

  const categoryColors = {
    travel: { bg: "bg-blue-50", text: "text-blue-600" },
    food: { bg: "bg-orange-50", text: "text-orange-600" },
    transport: { bg: "bg-indigo-50", text: "text-indigo-600" },
    office: { bg: "bg-purple-50", text: "text-purple-600" },
    default: { bg: "bg-primary-50", text: "text-primary-600" },
  };

  const getCategoryColor = (categoryName) => {
    if (!categoryName) return categoryColors.default;
    const key = categoryName.toLowerCase();
    return categoryColors[key] || categoryColors.default;
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader breadcrumbs={items} />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className={`bg-white p-5 rounded-xl border border-borderColor border-l-[3px] ${card.accentColor} hover:shadow-sm transition-shadow`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-2">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">
                  {card.value}
                </p>
                {card.subtext && (
                  <p className="text-sm text-gray-500">{card.subtext}</p>
                )}
                {card.change !== undefined && card.change !== 0 && (
                  <span
                    className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                      card.change > 0
                        ? "bg-green-50 text-green-600"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    <FiTrendingUp className="text-[10px]" />
                    {card.change > 0 ? "+" : ""}
                    {card.change}% from last month
                  </span>
                )}
              </div>
              <div
                className={`w-12 h-12 rounded-full ${card.bgColor} ring-4 ${card.ringColor} flex items-center justify-center`}
              >
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Quick Actions
        </h2>
        <div
          className={`grid gap-4 ${
            quickActions.length === 3
              ? "grid-cols-1 md:grid-cols-3"
              : quickActions.length === 2
              ? "grid-cols-1 md:grid-cols-2"
              : "grid-cols-1 max-w-sm"
          }`}
        >
          {quickActions.map((action, index) => (
            <div
              key={index}
              onClick={action.onClick}
              className={`p-5 rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${
                action.primary
                  ? "bg-gradient-to-br from-primary-600 to-primary-700 text-white"
                  : "bg-white border border-borderColor hover:border-gray-200"
              }`}
            >
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center mb-3 ${
                  action.primary
                    ? "bg-white/20"
                    : action.iconBg || "bg-gray-50"
                }`}
              >
                {action.icon}
              </div>
              <p
                className={`font-semibold text-sm ${
                  action.primary ? "text-white" : "text-gray-900"
                }`}
              >
                {action.title}
              </p>
              <p
                className={`text-xs mt-1 ${
                  action.primary ? "text-white/80" : "text-gray-500"
                }`}
              >
                {action.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="bg-white p-5 rounded-xl border border-borderColor">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Expenses
          </h2>
          <DefaultButton
            name="View All"
            variant="outline"
            size="sm"
            onClick={() => navigate("/reimbursement/my-expenses")}
          />
        </div>

        {recentExpenses && Array.isArray(recentExpenses) && recentExpenses.length > 0 ? (
          <div className="space-y-3">
            {recentExpenses.map((expense) => {
              const catColor = getCategoryColor(expense.category?.name);
              return (
                <div
                  key={expense.uuid}
                  className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() =>
                    navigate(`/reimbursement/expenses/${expense.uuid}`, { state: { from: 'my-expenses' } })
                  }
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-full ${catColor.bg} flex items-center justify-center`}>
                      <FiFileText className={`${catColor.text} text-lg`} />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">
                        {expense.expense_number}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {expense.category?.name || "-"} &middot; {expense.vendor || "-"}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {moment(expense.expense_date).format("DD MMM YYYY")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-base font-bold text-gray-900">
                      ₹{expense.amount?.toLocaleString()}
                    </p>
                    <div className="mt-1 flex justify-end">
                      {(() => {
                        const normalizedStatus = expense.status
                          ? String(expense.status).toUpperCase().trim()
                          : "DRAFT";
                        const cfg = statusConfig[normalizedStatus] || {
                          label: normalizedStatus,
                          color: "gray",
                        };
                        return (
                          <DefaultBadge
                            name={cfg.label}
                            color={cfg.color}
                            size="sm"
                          />
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-12 flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-4">
              <FiFileText className="text-gray-300 text-3xl" />
            </div>
            <p className="text-sm font-medium text-gray-500 mb-1">
              No recent expenses
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Your submitted expenses will appear here
            </p>
            <DefaultButton
              name="Go to My Expenses"
              variant="primary"
              size="sm"
              onClick={() => navigate("/reimbursement/my-expenses")}
            />
          </div>
        )}
      </div>

      {/* Policy Compliance */}
      {summary?.policy_summary && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Policy Compliance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-100 border-t-[3px] border-t-green-500 p-5 text-center">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-2">
                <FiShield className="text-green-600 text-lg" />
              </div>
              <p className="text-3xl font-bold text-green-600">
                {summary.policy_summary.compliant_count}
              </p>
              <p className="text-sm text-gray-500 mt-1">Within Policy</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 border-t-[3px] border-t-orange-500 p-5 text-center">
              <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-2">
                <FiAlertTriangle className="text-orange-600 text-lg" />
              </div>
              <p className="text-3xl font-bold text-orange-600">
                {summary.policy_summary.exceeds_count}
              </p>
              <p className="text-sm text-gray-500 mt-1">Exceeds Limit</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 border-t-[3px] border-t-blue-500 p-5 text-center">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-2">
                <FiActivity className="text-blue-600 text-lg" />
              </div>
              <p className="text-3xl font-bold text-blue-600">
                {summary.policy_summary.avg_approval_time}h
              </p>
              <p className="text-sm text-gray-500 mt-1">Avg. Approval Time</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReimbursementDashboard;
